/* /crm/organisaties/:orgId — één Organisatie op één pagina, zoals een project
 * (ADR 0022).
 *
 *   kop          terug, vorige/volgende (in de volgorde van de lijst, [ en ]), acties;
 *                logo, naam, status; sector, plaats, telefoon, e-mail, KvK; de feiten
 *   sectiebalk   Contactpersonen, Projecten, Offertes, Locaties, Charging,
 *                Piekanalyses, Gegevens (alleen wat er is, behalve de eerste drie)
 *
 * Offertes zijn die op de organisatie zelf én die op een van haar projecten. Locaties
 * lopen via de projecten (een Site hangt aan een project, niet aan een organisatie). */

import { useRef, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { Archive, ExternalLink, Globe, Mail, MapPin, MoreHorizontal, Pencil, Phone, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { OrganizationModal } from '@/components/crm/OrganizationModal';
import { ContactModal } from '@/components/crm/ContactModal';
import { OrganizationAnalysesList } from '@/components/piekanalyse/OrganizationAnalysesList';
import { Sectiebalk, type Sectie } from '@/features/projectpagina/ui/Sectiebalk';
import { euro } from '@/features/projectpagina/ui/opmaak';
import { isOpen, isVerlopen, lokaleDag } from '@/features/offertes/data/status';
import { haalOrganisatie, paginaStaat, type OrganisatiePaginaData } from './data/pagina';
import { organisatieLijst, type RelatieClient } from './data/organisaties';
import { archiveerBezwaar, archiveerOrganisatie, ArchiveerWeigering } from './data/archiveren';
import { adresRegel, classificatieLabel, domein, plaatsVan, statusLabel, STATUS_KLEUR, webadres } from './data/labels';
import { naRelatieWijziging, organisatieSleutel } from './data/sleutels';
import { Deel, Feit, Gegevenslijst, Navigatie, PaginaMelding, Pil, useRechten } from './ui/Onderdelen';
import { ChargingRijen, ContactRijen, LocatieRijen, OfferteRijen, ProjectRijen } from './ui/Blokken';
import { OrgIcoon } from './ui/Lijst';
import { Archiveer } from './ui/Archiveer';

const client = supabase as unknown as RelatieClient;
const DATUM = new Intl.DateTimeFormat('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' });
const pad = (id: string) => `/crm/organisaties/${id}`;

function useTenants(aan: boolean) {
  return useQuery({
    queryKey: ['tenants', 'kort'],
    enabled: aan,
    staleTime: 10 * 60_000,
    queryFn: async (): Promise<Map<string, string>> => {
      const { data, error } = await client.from('tenants').select('id, name');
      if (error) throw error;
      return new Map((data ?? []).map((t: any) => [t.id, t.name]));
    },
  });
}

function Kop({ d, kopRef, magBewerken, magBeheren, onBewerk, onArchiveer }: {
  d: OrganisatiePaginaData;
  kopRef: React.Ref<HTMLElement>;
  magBewerken: boolean;
  magBeheren: boolean;
  onBewerk: () => void;
  onArchiveer: () => void;
}) {
  const o = d.organisatie;
  const plaats = plaatsVan(o.adressen);
  /* Open is concept plus verstuurd, ook verlopen (zoals de offertelijst); hoeveel verlopen staat erbij. */
  const open = d.offertes.filter((q) => isOpen(q));
  const openBedrag = open.reduce((s, q) => s + (q.bedrag ?? 0), 0);
  const vandaag = lokaleDag();
  const verlopen = open.filter((q) => isVerlopen(q, vandaag)).length;
  const onderregel: ReactNode[] = [
    o.sector && <span key="s">{o.sector}</span>,
    plaats && <span key="p" className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" aria-hidden />{plaats}</span>,
    o.telefoon && <a key="t" href={`tel:${o.telefoon.replace(/[^\d+]/g, '')}`} className="inline-flex items-center gap-1 hover:text-foreground"><Phone className="h-3.5 w-3.5" aria-hidden />{o.telefoon}</a>,
    o.email && <a key="e" href={`mailto:${o.email}`} className="inline-flex items-center gap-1 hover:text-foreground"><Mail className="h-3.5 w-3.5" aria-hidden />{o.email}</a>,
    o.kvk && <span key="k">KvK {o.kvk}</span>,
  ].filter(Boolean);

  return (
    <section ref={kopRef} data-org-kop aria-label="Organisatie" className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Navigatie id={o.id} geheugen={organisatieLijst} terug="Organisaties" pad={pad} enkelvoud="organisatie" />
        <div className="flex items-center gap-2">
          {o.website && (
            <Button variant="outline" size="sm" className="h-8" asChild>
              <a href={webadres(o.website)} target="_blank" rel="noreferrer">
                <Globe className="mr-1.5 h-4 w-4" />
                {domein(o.website)}
              </a>
            </Button>
          )}
          {magBewerken && (
            <Button size="sm" className="h-8" onClick={onBewerk}>
              <Pencil className="mr-1.5 h-4 w-4" />
              Bewerken
            </Button>
          )}
          {magBeheren && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" className="h-8 w-8" aria-label="Meer acties">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <Archiveer
                  wat="organisatie"
                  naam={o.naam}
                  bezwaar={archiveerBezwaar(d.projecten.length)}
                  uitleg={d.contacten.length ? `De ${d.contacten.length === 1 ? 'contactpersoon blijft' : `${d.contacten.length} contactpersonen blijven`} staan in Contacten.` : undefined}
                  onArchiveer={onArchiveer}
                  trigger={
                    <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                      <Archive className="mr-2 h-4 w-4" />Archiveren
                    </DropdownMenuItem>
                  }
                />
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      <div className="flex min-w-0 items-start gap-3">
        <OrgIcoon src={o.logo} naam={o.naam} className="mt-0.5 h-10 w-10 rounded-lg border bg-background p-1 text-base" />
        <div className="flex min-w-0 flex-col gap-0.5">
          <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
            <h1 className="min-w-0 text-2xl font-semibold leading-tight tracking-tight">{o.naam}</h1>
            <Pil className={STATUS_KLEUR[o.status ?? ''] ?? 'bg-muted'}>{statusLabel(o.status)}</Pil>
            {o.classificatie === 'leverancier' && <Pil className="border-violet-200 bg-violet-50 text-violet-800">Leverancier</Pil>}
          </div>
          {onderregel.length > 0 && (
            <p className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">{onderregel}</p>
          )}
        </div>
      </div>

      {o.beschrijving && <p className="line-clamp-2 max-w-3xl text-sm text-muted-foreground" title={o.beschrijving}>{o.beschrijving}</p>}

      <dl className="m-0 flex flex-wrap items-center gap-x-4 gap-y-2">
        <Feit label="Projecten">{d.projecten.length}</Feit>
        <Feit label="Open offertes">
          {open.length ? `${open.length} · ${euro(openBedrag)}` : '0'}
          {verlopen > 0 && <span className="ml-1.5 text-xs font-medium text-red-700">{verlopen} verlopen</span>}
        </Feit>
        <Feit label="Contactpersonen">{d.contacten.length}</Feit>
        {d.locaties.length > 0 && <Feit label="Locaties">{d.locaties.length}</Feit>}
        {d.kansen.length + d.kavels.length > 0 && <Feit label="Charging">{d.kansen.length + d.kavels.length}</Feit>}
      </dl>
    </section>
  );
}

function Gegevens({ d, tenants, magBeheren }: { d: OrganisatiePaginaData; tenants: Map<string, string> | undefined; magBeheren: boolean }) {
  const o = d.organisatie;
  const rijen: [string, ReactNode][] = [
    ['Adres', o.adressen.length ? o.adressen.map((a, i) => <div key={i}>{adresRegel(a) || '—'}</div>) : '—'],
    ['Website', o.website ? <a className="inline-flex items-center gap-1 hover:underline" href={webadres(o.website)} target="_blank" rel="noreferrer">{domein(o.website)}<ExternalLink className="h-3 w-3" /></a> : '—'],
    ['Telefoon', o.telefoon ? <a className="hover:underline" href={`tel:${o.telefoon.replace(/[^\d+]/g, '')}`}>{o.telefoon}</a> : '—'],
    ['E-mail', o.email ? <a className="hover:underline" href={`mailto:${o.email}`}>{o.email}</a> : '—'],
    ['KvK', o.kvk ? <span className="font-mono">{o.kvk}</span> : '—'],
    ['Btw', o.btw ? <span className="font-mono">{o.btw}</span> : '—'],
    ['Sector', o.sector ?? '—'],
    ['Soort', classificatieLabel(o.classificatie)],
    ['Status', statusLabel(o.status)],
  ];
  if (magBeheren && o.zichtbaarVoor.length) {
    rijen.push(['Zichtbaar voor', tenants ? o.zichtbaarVoor.map((t) => tenants.get(t) ?? 'Onbekende tenant').join(', ') : '…']);
  }
  rijen.push(['In het CRM sinds', o.aangemaakt ? DATUM.format(new Date(o.aangemaakt)) : '—']);
  return <Gegevenslijst rijen={rijen} />;
}

export default function OrganisatiePagina() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { toast } = useToast();
  const { magBewerken, magBeheren } = useRechten();
  const kopRef = useRef<HTMLElement>(null);
  const [bewerken, setBewerken] = useState(false);
  const [nieuwContact, setNieuwContact] = useState(false);
  const q = useQuery({
    queryKey: organisatieSleutel(orgId),
    queryFn: () => haalOrganisatie(client, orgId as string),
    retry: (n, fout: any) => fout?.soort === 'fout' && n < 1,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
  const tenants = useTenants(magBeheren && Boolean(q.data?.organisatie.zichtbaarVoor.length));
  const staat = paginaStaat(q);

  if (staat.soort !== 'klaar') {
    return (
      <PaginaMelding
        soort={staat.soort}
        fout={'fout' in staat ? staat.fout : undefined}
        lijst={organisatieLijst.adres()}
        lijstLabel="Organisaties"
        opnieuw={staat.soort === 'fout' ? () => q.refetch() : undefined}
      />
    );
  }

  const d = staat.data;
  const o = d.organisatie;
  const archiveer = async () => {
    try {
      await archiveerOrganisatie(client, o.id);
      toast({ title: 'Organisatie gearchiveerd', description: `"${o.naam}" staat in het Archief.` });
      naRelatieWijziging(qc);
      navigate(organisatieLijst.adres());
    } catch (e) {
      toast({
        title: e instanceof ArchiveerWeigering ? 'Niet gearchiveerd' : 'Archiveren mislukt',
        description: (e as Error)?.message ?? String(e),
        variant: e instanceof ArchiveerWeigering ? undefined : 'destructive',
      });
      naRelatieWijziging(qc);
    }
  };

  const charging = d.kansen.length + d.kavels.length;
  const secties: Sectie[] = [
    { id: 'contacten', label: 'Contactpersonen', aantal: d.contacten.length },
    { id: 'projecten', label: 'Projecten', aantal: d.projecten.length },
    { id: 'offertes', label: 'Offertes', aantal: d.offertes.length },
    ...(d.locaties.length ? [{ id: 'locaties', label: 'Locaties', aantal: d.locaties.length }] : []),
    ...(charging ? [{ id: 'charging', label: 'Charging', aantal: charging }] : []),
    ...(d.piekanalyses ? [{ id: 'piekanalyses', label: 'Piekanalyses', aantal: d.piekanalyses }] : []),
    { id: 'gegevens', label: 'Gegevens' },
  ];

  return (
    <div className="flex flex-col gap-4" data-relatie-staat="klaar">
      <Kop d={d} kopRef={kopRef} magBewerken={magBewerken} magBeheren={magBeheren} onBewerk={() => setBewerken(true)} onArchiveer={archiveer} />
      <Sectiebalk secties={secties} kop={kopRef} titel={o.naam} label="Secties van deze organisatie" />
      <Deel
        id="contacten"
        titel="Contactpersonen"
        actie={magBewerken && (
          <Button size="sm" variant="outline" className="h-8" onClick={() => setNieuwContact(true)}>
            <UserPlus className="mr-1.5 h-4 w-4" />
            Nieuw contact
          </Button>
        )}
      >
        <ContactRijen contacten={d.contacten} leeg="Nog geen contactpersonen." />
      </Deel>
      <Deel id="projecten" titel="Projecten">
        <ProjectRijen projecten={d.projecten} leeg="Nog geen projecten voor deze organisatie." />
      </Deel>
      <Deel id="offertes" titel="Offertes">
        <OfferteRijen offertes={d.offertes} leeg="Nog geen offertes." />
      </Deel>
      {d.locaties.length > 0 && (
        <Deel id="locaties" titel="Locaties">
          <LocatieRijen locaties={d.locaties} />
        </Deel>
      )}
      {charging > 0 && (
        <Deel id="charging" titel="Charging">
          <ChargingRijen kansen={d.kansen} kavels={d.kavels} />
        </Deel>
      )}
      {d.piekanalyses > 0 && (
        <Deel id="piekanalyses" titel="Piekanalyses">
          <OrganizationAnalysesList organizationId={o.id} />
        </Deel>
      )}
      <Deel id="gegevens" titel="Gegevens">
        <Gegevens d={d} tenants={tenants.data} magBeheren={magBeheren} />
      </Deel>

      {magBewerken && (
        <>
          <OrganizationModal
            isOpen={bewerken}
            onClose={() => setBewerken(false)}
            organization={o.bron as any}
            onSave={() => naRelatieWijziging(qc)}
          />
          <ContactModal
            isOpen={nieuwContact}
            onClose={() => setNieuwContact(false)}
            contact={{ org_id: o.id } as any}
            onSave={() => naRelatieWijziging(qc)}
          />
        </>
      )}
    </div>
  );
}
